import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useClockContext } from '../app';
import { LayoutSite } from '../components/layout';
import { get_analysis_datasinfos, get_analysis_trackerdelta, get_datas } from '../services/api.service';
import { ApiAnalysisDatasInfos, ApiDeltaPoint } from '../types';
import './delta.scss';

/* ── Track colours ────────────────────────────────────────────────────────── */
const TRACK_COLORS = [
    '#e74c3c','#3498db','#2ecc71','#f39c12',
    '#9b59b6','#1abc9c','#e67e22','#e91e63',
    '#00bcd4','#8bc34a','#ff5722','#607d8b',
];
const trackerColor = (n: number) => TRACK_COLORS[Math.abs(n) % TRACK_COLORS.length];

/* ── Chart layout constants ───────────────────────────────────────────────── */
const PAD_L = 72, PAD_R = 16, PAD_T = 16, PAD_B = 36;

/* ── Axis helpers ─────────────────────────────────────────────────────────── */
function fmtRelTime(ms: number): string
{
    const neg = ms < 0;
    const s   = Math.floor(Math.abs(ms) / 1000);
    const h   = Math.floor(s / 3600);
    const m   = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const str = h > 0
        ? `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
        : `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    return neg ? '-' + str : str;
}

function niceStep(span: number, targetTicks: number): number
{
    if (span <= 0 || !isFinite(span)) return 1;
    const raw  = span / targetTicks;
    const mag  = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / mag;
    if (norm < 1.5) return mag;
    if (norm < 3.5) return 2 * mag;
    if (norm < 7.5) return 5 * mag;
    return 10 * mag;
}

/* ── View range ───────────────────────────────────────────────────────────── */
interface ViewRange { minTs: number; maxTs: number; }

/* ══════════════════════════════════════════════════════════════════════════════
   Component
══════════════════════════════════════════════════════════════════════════════ */

export const DeltaPage: FC = () =>
{
    const clock = useClockContext();

    /* ── UI state ──────────────────────────────────────────────────────────── */
    const [allDatas,    setAllDatas]    = useState<string[]>([]);
    const [seldatas,    setSelDatas]    = useState<string>('');
    const [moteur,      setMoteur]      = useState<ApiAnalysisDatasInfos | null>(null);
    const [expFusions,  setExpFusions]  = useState<Set<number>>(new Set());
    const [selTrackers, setSelTrackers] = useState<Set<number>>(new Set());

    /* ── Refs ──────────────────────────────────────────────────────────────── */
    const canvasRef        = useRef<HTMLCanvasElement>(null);
    const containerRef     = useRef<HTMLDivElement>(null);
    const deltaDataRef     = useRef<Map<number, ApiDeltaPoint[]>>(new Map());
    const selTrackersRef   = useRef<Set<number>>(new Set());
    const moteurRef        = useRef<ApiAnalysisDatasInfos | null>(null);
    // zoom / pan state
    const viewRef          = useRef<ViewRange | null>(null);
    const fullRangeRef     = useRef<ViewRange | null>(null);
    const hasManualZoomRef = useRef(false);
    const dragRef          = useRef<{ x: number; minTs: number; maxTs: number } | null>(null);

    /* ── Sync refs ─────────────────────────────────────────────────────────── */
    useEffect(() => { moteurRef.current = moteur; }, [moteur]);

    /* ── updateFullRange — recompute data bounds from selected+loaded trackers ── */
    const updateFullRange = useCallback(() =>
    {
        const sel = selTrackersRef.current;
        let minTs = Infinity, maxTs = -Infinity;
        deltaDataRef.current.forEach((pts, tracker) =>
        {
            if (!sel.has(tracker) || pts.length === 0) return;
            if (pts[0].ts          < minTs) minTs = pts[0].ts;
            if (pts[pts.length - 1].ts > maxTs) maxTs = pts[pts.length - 1].ts;
        });
        fullRangeRef.current = isFinite(minTs) ? { minTs, maxTs } : null;
    }, []);

    /* ── Chart drawing ─────────────────────────────────────────────────────── */
    const redraw = useCallback(() =>
    {
        const canvas = canvasRef.current;
        if (!canvas || canvas.width === 0 || canvas.height === 0) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const W = canvas.width, H = canvas.height;
        const chartW = W - PAD_L - PAD_R;
        const chartH = H - PAD_T - PAD_B;

        // Background
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.fillRect(PAD_L, PAD_T, chartW, chartH);

        // Gather visible datasets
        const sel = selTrackersRef.current;
        const datasets: { tracker: number; pts: ApiDeltaPoint[] }[] = [];
        deltaDataRef.current.forEach((pts, tracker) =>
        {
            if (sel.has(tracker) && pts.length > 0) datasets.push({ tracker, pts });
        });

        if (datasets.length === 0)
        {
            ctx.fillStyle = 'rgba(255,255,255,0.20)';
            ctx.font = '13px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('Aucun tracker sélectionné', W / 2, H / 2);
            return;
        }

        const view = viewRef.current;
        if (!view) return;

        const { minTs, maxTs } = view;
        const tsSpan = maxTs - minTs || 1;

        // Y range: only from points inside the visible window (auto-scale on zoom)
        let minDelta = Infinity, maxDelta = -Infinity;
        for (const { pts } of datasets)
        {
            for (let i = 0; i < pts.length; i++)
            {
                const p = pts[i];
                if (p.ts < minTs || p.ts > maxTs) continue;
                if (p.delta < minDelta) minDelta = p.delta;
                if (p.delta > maxDelta) maxDelta = p.delta;
            }
        }
        if (!isFinite(minDelta)) { minDelta = -100; maxDelta = 100; }

        const dy = (maxDelta - minDelta) * 0.12 || 100;
        minDelta -= dy; maxDelta += dy;
        if (minDelta > 0) minDelta = 0;
        if (maxDelta < 0) maxDelta = 0;
        const deltaSpan = maxDelta - minDelta || 1;

        // Origin for relative-time labels = full session start (stable while panning)
        const sessionStart = fullRangeRef.current?.minTs ?? minTs;

        const toX = (ts: number) => PAD_L + (ts - minTs) / tsSpan * chartW;
        const toY = (d: number)  => PAD_T + (1 - (d - minDelta) / deltaSpan) * chartH;

        // ── Y-axis grid + labels ──────────────────────────────────────────────
        const yStep  = niceStep(deltaSpan, 6);
        const yStart = Math.ceil(minDelta / yStep) * yStep;
        ctx.font         = '11px monospace';
        ctx.textAlign    = 'right';
        ctx.textBaseline = 'middle';
        for (let d = yStart; d <= maxDelta + yStep * 0.01; d += yStep)
        {
            const y = toY(d);
            if (y < PAD_T - 1 || y > PAD_T + chartH + 1) continue;
            ctx.strokeStyle = 'rgba(255,255,255,0.07)';
            ctx.lineWidth   = 1;
            ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(PAD_L + chartW, y); ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,0.45)';
            ctx.fillText(Math.round(d) + ' ms', PAD_L - 6, y);
        }

        // ── X-axis labels (relative to session start) ──────────────────────────
        const xStep  = niceStep(tsSpan, Math.max(3, Math.floor(chartW / 100)));
        const xStart = Math.ceil(minTs / xStep) * xStep;
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle    = 'rgba(255,255,255,0.45)';
        ctx.strokeStyle  = 'rgba(255,255,255,0.07)';
        for (let ts = xStart; ts <= maxTs + xStep * 0.01; ts += xStep)
        {
            const x = toX(ts);
            if (x < PAD_L - 1 || x > PAD_L + chartW + 1) continue;
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(x, PAD_T); ctx.lineTo(x, PAD_T + chartH); ctx.stroke();
            ctx.fillText(fmtRelTime(ts - sessionStart), x, PAD_T + chartH + 6);
        }

        // ── Zero reference line ───────────────────────────────────────────────
        if (minDelta <= 0 && maxDelta >= 0)
        {
            const y0 = toY(0);
            ctx.strokeStyle = 'rgba(255,255,255,0.28)';
            ctx.lineWidth   = 1;
            ctx.setLineDash([5, 4]);
            ctx.beginPath(); ctx.moveTo(PAD_L, y0); ctx.lineTo(PAD_L + chartW, y0); ctx.stroke();
            ctx.setLineDash([]);
        }

        // ── Chart border ──────────────────────────────────────────────────────
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth   = 1;
        ctx.strokeRect(PAD_L, PAD_T, chartW, chartH);

        // ── Draw each tracker line (clipped) ─────────────────────────────────
        ctx.save();
        ctx.beginPath();
        ctx.rect(PAD_L, PAD_T, chartW, chartH);
        ctx.clip();

        for (const { tracker, pts } of datasets)
        {
            ctx.strokeStyle = trackerColor(tracker);
            ctx.lineWidth   = 1.5;
            ctx.lineJoin    = 'round';
            ctx.beginPath();
            let started = false;
            for (let i = 0; i < pts.length; i++)
            {
                const x = toX(pts[i].ts);
                const y = toY(pts[i].delta);
                if (!started) { ctx.moveTo(x, y); started = true; }
                else          { ctx.lineTo(x, y); }
            }
            ctx.stroke();
        }
        ctx.restore();

        // ── Legend ────────────────────────────────────────────────────────────
        const LEG_ITEM_W = 52;
        let lx = PAD_L + 8, ly = PAD_T + 10;
        ctx.font         = '11px monospace';
        ctx.textBaseline = 'middle';
        ctx.textAlign    = 'left';
        for (const { tracker } of datasets)
        {
            const color = trackerColor(tracker);
            ctx.fillStyle = color;
            ctx.fillRect(lx, ly - 1, 14, 3);
            ctx.fillStyle = 'rgba(255,255,255,0.80)';
            ctx.fillText(`#${tracker}`, lx + 18, ly);
            lx += LEG_ITEM_W;
            if (lx + LEG_ITEM_W > PAD_L + chartW - 4) { lx = PAD_L + 8; ly += 16; }
        }

    }, []); // stable — reads only refs

    /* ── fitView — reset to full data range ────────────────────────────────── */
    const fitView = useCallback(() =>
    {
        hasManualZoomRef.current = false;
        const full = fullRangeRef.current;
        if (full) viewRef.current = { ...full };
        redraw();
    }, [redraw]);

    /* ── zoomX — zoom around pixel cx on the canvas ────────────────────────── */
    const zoomX = useCallback((factor: number, cx: number) =>
    {
        const view = viewRef.current;
        const full = fullRangeRef.current;
        if (!view || !full) return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const chartW  = canvas.width - PAD_L - PAD_R;
        const ratio   = Math.max(0, Math.min(1, (cx - PAD_L) / chartW));
        const span    = view.maxTs - view.minTs;
        const pivot   = view.minTs + ratio * span;
        const newSpan = span * factor;
        const fullSpan = full.maxTs - full.minTs;

        if (newSpan >= fullSpan)
        {
            // Zoomed all the way out — snap back to full range
            viewRef.current = { ...full };
            hasManualZoomRef.current = false;
        }
        else
        {
            let newMin = pivot - ratio * newSpan;
            let newMax = pivot + (1 - ratio) * newSpan;
            if (newMin < full.minTs) { newMin = full.minTs; newMax = full.minTs + newSpan; }
            if (newMax > full.maxTs) { newMax = full.maxTs; newMin = full.maxTs - newSpan; }
            viewRef.current = { minTs: newMin, maxTs: newMax };
            hasManualZoomRef.current = true;
        }
        redraw();
    }, [redraw]);

    /* ── Mouse handlers (pan) ──────────────────────────────────────────────── */
    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) =>
    {
        const view = viewRef.current;
        if (!view) return;
        dragRef.current = { x: e.clientX, minTs: view.minTs, maxTs: view.maxTs };
        e.currentTarget.style.cursor = 'grabbing';
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) =>
    {
        const drag = dragRef.current;
        if (!drag) return;
        const full   = fullRangeRef.current;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const chartW = canvas.width - PAD_L - PAD_R;
        const span   = drag.maxTs - drag.minTs;
        const dtMs   = -(e.clientX - drag.x) / chartW * span;

        let newMin = drag.minTs + dtMs;
        let newMax = drag.maxTs + dtMs;
        if (full)
        {
            if (newMin < full.minTs) { newMin = full.minTs; newMax = full.minTs + span; }
            if (newMax > full.maxTs) { newMax = full.maxTs; newMin = full.maxTs - span; }
        }

        viewRef.current = { minTs: newMin, maxTs: newMax };
        hasManualZoomRef.current = true;
        redraw();
    }, [redraw]);

    const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) =>
    {
        dragRef.current = null;
        e.currentTarget.style.cursor = 'grab';
    }, []);

    const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLCanvasElement>) =>
    {
        dragRef.current = null;
        e.currentTarget.style.cursor = 'grab';
    }, []);

    /* ── Wheel zoom (non-passive so we can preventDefault) ─────────────────── */
    useEffect(() =>
    {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const onWheel = (e: WheelEvent) =>
        {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            zoomX(e.deltaY > 0 ? 1.25 : 0.8, e.clientX - rect.left);
        };
        canvas.addEventListener('wheel', onWheel, { passive: false });
        return () => canvas.removeEventListener('wheel', onWheel);
    }, [zoomX]);

    /* ── Canvas resize observer ────────────────────────────────────────────── */
    useEffect(() =>
    {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries =>
        {
            const { width, height } = entries[0].contentRect;
            const canvas = canvasRef.current;
            if (!canvas) return;
            canvas.width  = Math.floor(width);
            canvas.height = Math.floor(height);
            redraw();
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [redraw]);

    /* ── Initial dataset list load ─────────────────────────────────────────── */
    useEffect(() =>
    {
        get_datas()
            .then(d =>
            {
                setAllDatas(d);
                if (d.length > 0) setSelDatas(d[0]);
            })
            .catch(e => message.error(String(e)));
    }, []);

    /* ── Load moteur when dataset changes ──────────────────────────────────── */
    useEffect(() =>
    {
        if (!seldatas) return;
        setMoteur(null);
        setExpFusions(new Set());
        setSelTrackers(new Set());
        selTrackersRef.current = new Set();
        deltaDataRef.current   = new Map();
        viewRef.current        = null;
        fullRangeRef.current   = null;
        hasManualZoomRef.current = false;
        redraw();

        get_analysis_datasinfos(seldatas)
            .then(info =>
            {
                if (!info) return;
                setMoteur(info);
                const open = new Set<number>();
                info.liste_fusion.forEach((fid, i) =>
                {
                    const trackers = (info.id_fusion[i] ?? []).filter(t => t !== -1);
                    if (trackers.some(t => info.trackers_with_gps.includes(t))) open.add(fid);
                });
                setExpFusions(open);
            })
            .catch(e => message.error(String(e)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seldatas]); // redraw/updateFullRange are stable refs, intentionally omitted

    /* ── Load delta data whenever selTrackers changes ──────────────────────── */
    useEffect(() =>
    {
        selTrackersRef.current = selTrackers;

        const applyView = () =>
        {
            updateFullRange();
            if (!hasManualZoomRef.current)
            {
                const full = fullRangeRef.current;
                if (full) viewRef.current = { ...full };
            }
            redraw();
        };

        const toLoad: number[] = [];
        selTrackers.forEach(t => { if (!deltaDataRef.current.has(t)) toLoad.push(t); });

        if (toLoad.length === 0) { applyView(); return; }

        Promise.all(toLoad.map(t =>
            get_analysis_trackerdelta(seldatas, t)
                .then(pts => { deltaDataRef.current.set(t, pts); })
                .catch(e => message.error(String(e)))
        )).then(applyView);
    }, [selTrackers, seldatas, redraw, updateFullRange]);

    /* ── Tracker / fusion toggles ──────────────────────────────────────────── */
    const toggleTracker = useCallback((tracker: number) =>
    {
        setSelTrackers(prev =>
        {
            const next = new Set(prev);
            if (next.has(tracker)) next.delete(tracker); else next.add(tracker);
            return next;
        });
    }, []);

    const selectAllTrackers = useCallback(() =>
    {
        const info = moteurRef.current;
        if (!info) return;
        setSelTrackers(new Set(info.trackers_with_gps));
    }, []);

    const deselectAllTrackers = useCallback(() =>
    {
        setSelTrackers(new Set());
    }, []);

    const toggleFusion = useCallback((fid: number) =>
    {
        setExpFusions(prev =>
        {
            const next = new Set(prev);
            if (next.has(fid)) next.delete(fid); else next.add(fid);
            return next;
        });
    }, []);

    /* ── Render ──────────────────────────────────────────────────────────────── */
    return (
        <LayoutSite>
            <div className="delta">

                {/* ── Header ──────────────────────────────────────────────── */}
                <div className="delta__header">
                    <span>Analyse Δ timestamp</span>
                    <select
                        className="delta__header__select"
                        value={seldatas}
                        onChange={e => setSelDatas(e.target.value)}
                    >
                        {allDatas.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <span className="delta__header__clock">{clock}</span>
                </div>

                {/* ── Body ────────────────────────────────────────────────── */}
                <div className="delta__body">

                    {/* Left panel */}
                    <div className="delta__body__panel">
                        <div className="delta__body__panel__toolbar">
                            <button
                                className="delta__body__panel__toolbar__btn"
                                title="Tout sélectionner"
                                onClick={selectAllTrackers}
                                disabled={!moteur}
                            >▣</button>
                            <button
                                className="delta__body__panel__toolbar__btn"
                                title="Tout désélectionner"
                                onClick={deselectAllTrackers}
                                disabled={!moteur}
                            >□</button>
                        </div>

                        {moteur && moteur.liste_fusion
                            .map((fid, i) => ({ fid, trackers: (moteur.id_fusion[i] ?? []).filter(t => t !== -1) }))
                            .sort((a, b) => a.fid - b.fid)
                            .map(({ fid, trackers }) =>
                        {
                            const exp = expFusions.has(fid);
                            return (
                                <div key={fid} className="delta__body__panel__fusion">
                                    <div
                                        className="delta__body__panel__fusion__header"
                                        onClick={() => toggleFusion(fid)}
                                    >
                                        <span className="delta__body__panel__fusion__header__arrow">
                                            {exp ? '▼' : '▶'}
                                        </span>
                                        <span>Fusion {fid}</span>
                                    </div>
                                    {exp && trackers.map(t =>
                                    {
                                        const hasData = moteur.trackers_with_gps.includes(t);
                                        const active  = selTrackers.has(t);
                                        if (!hasData)
                                        {
                                            return (
                                                <div key={t} className="delta__body__panel__fusion__tracker--disabled">
                                                    <span className="delta__body__panel__fusion__tracker__dot"
                                                        style={{ backgroundColor: trackerColor(t) }} />
                                                    #{t}
                                                </div>
                                            );
                                        }
                                        return (
                                            <div
                                                key={t}
                                                className={active
                                                    ? 'delta__body__panel__fusion__tracker--active'
                                                    : 'delta__body__panel__fusion__tracker'}
                                                onClick={() => toggleTracker(t)}
                                            >
                                                <span className="delta__body__panel__fusion__tracker__dot"
                                                    style={{ backgroundColor: trackerColor(t) }} />
                                                #{t}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>

                    {/* Chart canvas + overlay controls */}
                    <div className="delta__body__chart" ref={containerRef}>
                        <canvas
                            ref={canvasRef}
                            style={{ cursor: 'grab' }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                        />
                        <div className="delta__body__chart__controls">
                            <button
                                className="delta__body__chart__btn"
                                title="Réinitialiser la vue"
                                onClick={fitView}
                            >⤢</button>
                        </div>
                    </div>
                </div>

            </div>
        </LayoutSite>
    );
};
